import React from 'react';
import { Download, Package, Snowflake } from 'lucide-react';

import { opsVisibilityApi } from 'api/opsVisibility';
import { useManifestData } from 'components/logistics/fulfillmentLifecycle/hooks/useManifestData';
import ManifestCard from 'components/logistics/fulfillmentLifecycle/modals/tabs/ManifestCard';
import ProductTable from 'components/logistics/fulfillmentLifecycle/modals/tabs/ProductTable';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Skeleton } from 'shadcn/components/ui/Skeleton';
import { downloadCSV } from 'shared/components/csvDownload';

const OOB_WARNING =
    'This manifest only includes orders that have ALREADY BEEN ROUTED. Pre-purchase or unrouted items will NOT appear.';
const FROZEN_WARNING =
    'This manifest shows a summary of frozen shipments grouped by route. Use the export button for each route to download individual shipment details.';

/**
 * ManifestsTab
 *
 * Tab content for the Route Detail Modal showing OOB and Frozen manifest data.
 * Fetches manifest data via useManifestData when rendered (lazy-loaded by Tabs).
 *
 * @param {Object} props
 * @param {number} props.lineHaulId - Line haul ID for the route
 * @param {string} props.packDate - Pack date in YYYY-MM-DD format
 */
function ManifestsTab({ lineHaulId, packDate }) {
    const { error, frozenManifest, loading, oobManifest } = useManifestData({ lineHaulId, packDate });

    if (loading) {
        return <ManifestsTabSkeleton />;
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertTitle>Error loading manifests</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (!oobManifest?.manifest && !frozenManifest?.manifest) {
        return (
            <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">No manifest data available for this route.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* OOB Manifest */}
            {oobManifest?.manifest && (
                <ManifestCard
                    icon={<Package className="h-5 w-5 text-orange-500" />}
                    title="OOB Manifest"
                    warningText={OOB_WARNING}
                    stats={[
                        { label: 'Expected Pack Date', value: oobManifest.manifest.expected_pack_date || '—' },
                        { label: 'Total Items', value: oobManifest.manifest.total_items ?? 0 },
                        { label: 'Generated At', value: oobManifest.manifest.generated_at || '—' },
                    ]}
                >
                    <ProductTable manifest={oobManifest.manifest} />
                </ManifestCard>
            )}

            {/* Frozen Manifest */}
            {frozenManifest?.manifest && (
                <ManifestCard
                    icon={<Snowflake className="h-5 w-5 text-blue-400" />}
                    title="Frozen Manifest"
                    warningText={FROZEN_WARNING}
                    stats={[
                        { label: 'Expected Pack Date', value: frozenManifest.manifest.expected_pack_date || '—' },
                        { label: 'Total Shipments', value: frozenManifest.manifest.total_shipments ?? 0 },
                        { label: 'Generated At', value: frozenManifest.manifest.generated_at || '—' },
                    ]}
                >
                    <FrozenRouteTable manifest={frozenManifest.manifest} />
                </ManifestCard>
            )}
        </div>
    );
}

/**
 * FrozenRouteTable
 *
 * Renders a table of frozen manifest routes with shipment counts
 * and per-route detailed CSV export.
 *
 * @param {Object} props
 * @param {Object} props.manifest - Frozen manifest object
 */
function FrozenRouteTable({ manifest }) {
    const { routes = [] } = manifest || {};

    if (!routes.length) {
        return <p className="text-sm text-muted-foreground py-4 text-center">No routes found for this manifest.</p>;
    }

    const handleDownloadSummaryCSV = () => {
        const headers = ['Route', 'Shipment Count'];
        let csvContent = `${headers.join(',')}\r\n`;

        routes.forEach((route) => {
            const row = [`"${(route.route || '').replace(/"/g, '""')}"`, route.shipmentCount || 0];
            csvContent += `${row.join(',')}\r\n`;
        });

        const filename = `Frozen_Manifest_${manifest.line_haul_id}_${manifest.expected_pack_date}.csv`;
        downloadCSV(csvContent, filename);
    };

    const handleDownloadDetailedCSV = async (route) => {
        try {
            await opsVisibilityApi.downloadFrozenManifestByRouteCsv({
                lineHaulId: manifest.line_haul_id,
                expectedPackDate: manifest.expected_pack_date,
                route,
            });
        } catch {
            // Download failures are surfaced by the browser's network layer
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Routes</span>
                    <Badge variant="secondary">{routes.length} routes</Badge>
                </div>
                <Button
                    onClick={handleDownloadSummaryCSV}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                >
                    <Download className="h-3.5 w-3.5" />
                    Summary CSV
                </Button>
            </div>

            <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="px-3 py-2 text-left font-medium">Route</th>
                            <th className="px-3 py-2 text-center font-medium">Shipment Count</th>
                            <th className="px-3 py-2 text-right font-medium">Export Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {routes.map((routeItem) => (
                            <tr key={routeItem.route} className="hover:bg-muted/30">
                                <td className="px-3 py-2">
                                    <Badge variant="outline">{routeItem.route}</Badge>
                                </td>
                                <td className="px-3 py-2 text-center font-medium">{routeItem.shipmentCount}</td>
                                <td className="px-3 py-2 text-right">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadDetailedCSV(routeItem.route)}
                                        className="h-7 px-2 flex items-center gap-1 ml-auto"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Detailed CSV
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Loading skeleton for the Manifests tab
 */
function ManifestsTabSkeleton() {
    return (
        <div className="space-y-4" data-testid="manifests-tab-skeleton">
            {[1, 2].map((i) => (
                <div key={i} className="rounded-lg border p-4 space-y-3">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-12 w-full" />
                    <div className="grid grid-cols-3 gap-3">
                        <Skeleton className="h-16" />
                        <Skeleton className="h-16" />
                        <Skeleton className="h-16" />
                    </div>
                    <Skeleton className="h-32 w-full" />
                </div>
            ))}
        </div>
    );
}

export default ManifestsTab;
